import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  C, PageHeader, GlassCard, Badge, Skeleton, Button, StatusDot, Input,
} from "@coremonit/design-system";
import { Layers, Search, ArrowUpRight } from "lucide-react";
import { useClusterK8sObjects } from "../api/hooks";
import { Breadcrumbs } from "../components/Breadcrumbs";
import type { K8sObjectKind } from "@coremonit/types";

const KINDS: Array<{ id: K8sObjectKind | "all"; label: string }> = [
  { id: "all", label: "All" },
  { id: "pod", label: "Pods" },
  { id: "deployment", label: "Deployments" },
  { id: "service", label: "Services" },
  { id: "ingress", label: "Ingress" },
  { id: "configmap", label: "ConfigMaps" },
  { id: "secret", label: "Secrets" },
  { id: "pvc", label: "PVCs" },
  { id: "hpa", label: "HPA" },
  { id: "job", label: "Jobs" },
  { id: "cronjob", label: "CronJobs" },
  { id: "event", label: "Events" },
];

export function K8sExplorerPage() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const kind = params.get("kind") ?? "all";
  const nsFilter = params.get("namespace");
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const objects = useClusterK8sObjects(id, kind === "all" ? undefined : kind);

  const filtered = useMemo(() => {
    let items = objects.data?.items ?? [];
    if (nsFilter) items = items.filter((o) => o.namespace === nsFilter);
    if (q.trim()) {
      const needle = q.toLowerCase();
      items = items.filter((o) => o.name.toLowerCase().includes(needle) || o.kind.includes(needle));
    }
    return items;
  }, [objects.data, nsFilter, q]);

  const namespaces = useMemo(
    () => [...new Set((objects.data?.items ?? []).map((o) => o.namespace))].sort(),
    [objects.data],
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Breadcrumbs items={[
        { label: "Organization", path: "/" },
        { label: id ?? "Cluster", path: `/clusters/${id}` },
        { label: "Kubernetes Explorer" },
      ]} />
      <PageHeader
        icon={<Layers size={16} />}
        title="Kubernetes Visual Explorer"
        subtitle={id ? `Browse every object in ${id} without kubectl` : ""}
        actions={
          <Button variant="secondary" iconRight={<ArrowUpRight size={11} />} onClick={() => nav(`/clusters/${id}`)}>
            Cluster overview
          </Button>
        }
      />

      <GlassCard style={{ padding: 12 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <Input value={q} onChange={setQ} prefix={<Search size={12} />} placeholder="Search objects…" />
          </div>
          <select
            value={nsFilter ?? ""}
            onChange={(e) => {
              const next = new URLSearchParams(params);
              if (e.target.value) next.set("namespace", e.target.value); else next.delete("namespace");
              setParams(next);
            }}
            style={{ background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, padding: "6px 10px", fontSize: 11 }}
          >
            <option value="">All namespaces</option>
            {namespaces.map((ns) => <option key={ns} value={ns}>{ns}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
          {KINDS.map((k) => (
            <button
              key={k.id}
              onClick={() => {
                const next = new URLSearchParams(params);
                if (k.id === "all") next.delete("kind"); else next.set("kind", k.id);
                setParams(next);
              }}
              style={{
                padding: "4px 10px", borderRadius: 8, fontSize: 10, fontWeight: 600, cursor: "pointer",
                background: (kind === k.id || (k.id === "all" && kind === "all")) ? C.blueDim : C.surfaceHigh,
                border: `1px solid ${(kind === k.id || (k.id === "all" && kind === "all")) ? C.borderBright : C.border}`,
                color: (kind === k.id || (k.id === "all" && kind === "all")) ? C.blue : C.textSub,
              }}
            >
              {k.label}
            </button>
          ))}
        </div>
      </GlassCard>

      {objects.isLoading ? (
        <Skeleton height={320} radius={16} />
      ) : (
        <GlassCard style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "100px 1fr 120px 80px 100px", gap: 8, padding: "10px 14px", borderBottom: `1px solid ${C.border}`, color: C.textMuted, fontSize: 9, fontWeight: 700, textTransform: "uppercase" }}>
            <span>Kind</span><span>Name</span><span>Namespace</span><span>Age</span><span>Status</span>
          </div>
          {filtered.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: C.textMuted, fontSize: 12 }}>No objects match your filters.</div>
          ) : filtered.map((o) => (
            <button
              key={`${o.kind}_${o.namespace}_${o.name}`}
              onClick={() => nav(o.path)}
              style={{
                width: "100%", display: "grid", gridTemplateColumns: "100px 1fr 120px 80px 100px", gap: 8,
                padding: "10px 14px", textAlign: "left", cursor: "pointer",
                background: "transparent", border: "none", borderBottom: `1px solid ${C.border}`,
                color: C.textSub, fontSize: 11,
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = C.surfaceHigh; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
            >
              <span style={{ color: C.textMuted, textTransform: "uppercase", fontSize: 9 }}>{o.kind}</span>
              <span style={{ color: C.text, fontWeight: 600 }}>{o.name}</span>
              <span style={{ color: C.textMuted }}>{o.namespace}</span>
              <span style={{ color: C.textMuted }}>{o.age}</span>
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <StatusDot status={o.status} /> {o.status}
              </span>
            </button>
          ))}
        </GlassCard>
      )}
    </div>
  );
}
